//#region IMPORTS
import {
  createInstoreOrderAsync,
  updateInstoreOrderAsync,
  updateManyInstoreOrderLinesAsync,
  fetchMatchingInstoreTemplateAsync,
} from "../../features/instoreOrders/instoreOrdersSlice";
import {
  InstoreOrder,
  FindTemplateRequest,
  InstoreOrderRequest,
  InstoreOrderLineRequest,
} from "../../models/instoreOrder";
import {
  ADD_COMMENT_MODAL,
  NONE_OPTION_VALUE,
  SEARCH_STORE_MODAL,
  ADD_TRACK_AND_TRACE_MODAL,
  INSTORE_ORDER_WITHOUT_EXTRAS_SURE,
} from "../../models/_consts";
import { Fragment, ReactNode, useEffect, useState } from "react";
import { parse, isValid as isValidDate, format } from "date-fns";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { instoreOrderTypesSelectors } from "../../features/instoreOrderTypes/instoreOrderTypesSlice";
import { MagnifyingGlassCircleIcon } from "@heroicons/react/20/solid";
import { digitalFrameSelectors } from "../../features/digitalFrames/digtalFramesSlice";
import { instoreTypesSelectors } from "../../features/instoreTypes/instoreTypesSlice";
import { FieldValues, useForm } from "react-hook-form";
import { languageSelectors } from "../../features/languages/languageSlice";
import { countrySelectors } from "../../features/countries/countriesSlice";
import { InstoreOrderType } from "../../models/instoreOrderType";
import { InstoreTemplate } from "../../models/instoreTemplate";
import { toBackendDate } from "../../helpers/toBackendDate";
import { InstoreType } from "../../models/instoreType";
import { StoreFormat } from "../../models/storeFormat";
import { useHistory } from "react-router-dom";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { TruckIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/16/solid";
import { Language } from "../../models/language";
import { Country } from "../../models/country";
import { toast } from "react-toastify";
import { Store } from "../../models/store";
import InstoreOrderFormTrackAndTraceBadge from "./InstoreOrderFormTrackAndTraceBadge";
import InstoreOrderFormCommentBadge from "./InstoreOrderFormCommentBadge";
import InstoreOrderSearchStoreForm from "./InstoreOrderSearchStoreForm";
import instoreOrderPresetComments from "../../hardData/instoreOrderPresetComments";
import MainFieldArrayForm from "../_layout/MainFieldArrayForm";
import useAdminManager from "../../hooks/useAdminManager";
import classNames from "../../helpers/classNames";
import AreYouSure from "../modalTypes/AreYouSure";
import MainModal from "../_layout/MainModal";
//#endregion

//#region INTERFACE
interface Props {
  instoreOrderLinesFromTemplate: InstoreOrderLineRequest[];
  setInstoreTemplateHandler: (template?: InstoreTemplate) => void;
  setStoreFormatHandler: (format: StoreFormat) => void;
  orderLineItemsList: ReactNode;
  instoreOrderLineRequests?: InstoreOrderLineRequest[];
  templateToUse?: InstoreTemplate;
  instoreOrder?: InstoreOrder;
  disabled?: boolean;
  isDirty?: boolean;
}
//#endregion

export default function InstoreOrderForm({
  instoreOrderLinesFromTemplate,
  setInstoreTemplateHandler,
  instoreOrderLineRequests,
  setStoreFormatHandler,
  orderLineItemsList,
  templateToUse,
  instoreOrder,
  disabled = false,
  isDirty = false,
}: Props) {
  //#region SETUP
  // Router
  const history = useHistory();

  // Redux
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(({ account }) => account);
  const { instoreTypes_Loaded, loading: instoretypesLoading } = useAppSelector(
    ({ instoreTypes }) => instoreTypes,
  );
  const { instoreOrderTypes_Loaded, loading: instoreOrderTypesLoading } =
    useAppSelector(({ instoreOrderTypes }) => instoreOrderTypes);
  const { languages_loaded, loading: languagesLoading } = useAppSelector(
    ({ languagesManagement }) => languagesManagement,
  );
  const { countries_loaded, loading: countriesLoading } = useAppSelector(
    ({ countriesManagement }) => countriesManagement,
  );
  const { loading: instoreOrderLoading } = useAppSelector(
    ({ instoreOrders }) => instoreOrders,
  );
  const instoreOrderTypes = useAppSelector(
    instoreOrderTypesSelectors.selectAll,
  );
  const digitalFrames = useAppSelector(digitalFrameSelectors.selectAll);
  const instoreTypes = useAppSelector(instoreTypesSelectors.selectAll);
  const languages = useAppSelector(languageSelectors.selectAll);
  const countries = useAppSelector(countrySelectors.selectAll);

  const isAdmin = useAdminManager(user);

  // Local state
  const [noExtrasProjects, setNoExtrasProjects] = useState<boolean>(false);
  const [missingPersoData, setMissingPersoData] = useState<boolean>(false);

  const [instoreType, setInstoreType] = useState<InstoreType>();
  const [digitalFramesLoaded] = useState<boolean>(true);
  const [storeToUse, setStoreToUse] = useState<Store>();
  const [pendingSubmitData, setPendingSubmitData] =
    useState<FieldValues | null>(null);

  const formDisabled =
    disabled ||
    languagesLoading ||
    countriesLoading ||
    instoretypesLoading ||
    instoreOrderLoading ||
    instoreOrderTypesLoading;

  // UseForm
  const {
    reset,
    watch,
    register,
    setValue,
    getValues,
    clearErrors,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({ mode: "all" });

  // Watch values
  const [
    orderTypeInstoreNameValue,
    storeOpeningDateValue,
    instoreTypeValue,
    storeFormatValue,
    comments,
    trackAndTrace,
    externalStoreIdValue,
    storeNameValue,
    storeAddresStreet1Value,
    storeAddresZipCodeValue,
    storeAddresCityValue,
    storeAddresCountryValue,
    storeLanguageValue,
    deliveryAddresStreet1Value,
    deliveryAddresZipCodeValue,
    deliveryAddresCityValue,
    deliveryAddresCountryValue,
    deliveryDateValue,
    templateValue,
    digitalFrameValue,
  ] = watch([
    "order_type_instore_name",
    "store_opening_date",
    "instore_type",
    "store_format",
    "comment",
    "track_and_trace",
    "external_store_id",
    "store_name",
    "store_addres_street_1",
    "store_addres_zip_code",
    "store_addres_city",
    "store_addres_country",
    "store_language",
    "delivery_addres_street_1",
    "delivery_addres_zip_code",
    "delivery_addres_city",
    "delivery_addres_country",
    "delivery_date",
    "template",
    "store_digital_frame",
  ]);
  //#endregion

  //#region SIDE EFFECTS
  useEffect(() => {
    if (isDirty) {
      reset(getValues());
    }
  }, [isDirty, reset, getValues]);

  // Fetch order data
  useEffect(() => {
    if (instoreOrder) {
      reset({
        ...instoreOrder,
        order_type_instore_id: String(instoreOrder.order_type_instore_id),
      });

      if (instoreOrder.delivery_date) {
        const deliveryDate = parse(
          instoreOrder.delivery_date,
          "dd/MM/yyyy HH:mm",
          new Date(),
        );
        if (isValidDate(deliveryDate)) {
          setValue("delivery_date", format(deliveryDate, "yyyy-MM-dd"));
        }
      }

      if (instoreOrder.store_opening_date) {
        const date = parse(
          instoreOrder.store_opening_date,
          "dd/MM/yyyy HH:mm",
          new Date(),
        );
        if (isValidDate(date)) {
          setValue("store_opening_date", format(date, "yyyy-MM-dd"));
        }
      }

      if (instoreOrder.order_date) {
        const orderDate = parse(
          instoreOrder.order_date,
          "dd/MM/yyyy HH:mm",
          new Date(),
        );
        if (isValidDate(orderDate)) {
          setValue("order_date", format(orderDate, "yyyy-MM-dd HH:mm"));
        }
      }
    } else {
      setValue("order_date", format(new Date(), "yyyy-MM-dd HH:mm"));
    }
  }, [instoreOrder, setValue, reset]);

  // Set instore type
  useEffect(() => {
    !!instoreTypeValue &&
      setInstoreType(instoreTypes.find((it) => it.name === instoreTypeValue));
  }, [instoreType, instoreTypeValue, instoreTypes]);

  // Set form after a existing store was selected
  useEffect(() => {
    if (storeToUse) {
      // Store information
      setValue("external_store_id", storeToUse.external_store_id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("store_name", storeToUse.store_name);
      setValue("store_addres_street_1", storeToUse.address_1);
      setValue("store_addres_street_2", storeToUse.address_2);
      setValue("store_addres_street_3", storeToUse.address_3);
      setValue("store_addres_zip_code", storeToUse.postal_code);
      setValue("store_addres_city", storeToUse.city);
      setValue("store_addres_country", storeToUse.country_name);
      setValue("store_language", storeToUse.language);
      setValue("store_contact_email", storeToUse.contact_email);
      setValue("store_digital_frame", storeToUse.digital_frame);

      // Delivery info
      setValue(
        "delivery_addres_street_1",
        storeToUse.delivery_address_address_1,
      );
      setValue(
        "delivery_addres_street_2",
        storeToUse.delivery_address_address_2,
      );
      setValue(
        "delivery_addres_street_3",
        storeToUse.delivery_address_address_3,
      );
      setValue(
        "delivery_addres_zip_code",
        storeToUse.delivery_address_postal_code,
      );
      setValue("delivery_addres_city", storeToUse.delivery_address_city);
      setValue(
        "delivery_addres_country",
        storeToUse.delivery_address_country_name,
      );
      setValue("store_contact_first_name", storeToUse.contact_first_name);
      setValue("store_contact_last_name", storeToUse.contact_last_name);
      setValue("store_contact_phone", storeToUse.contact_phone);
      setValue("delivery_contact_email", storeToUse.contact_email);
    }
  }, [storeToUse, setValue, clearErrors]);
  //#endregion

  //#region SUBMIT FORM
  // Check the perso values
  function allPersoValuesFilled(arr: InstoreOrderLineRequest[]): boolean {
    for (const item of arr) {
      if (!item.custom_fields) {
        return false;
      }

      for (const field of item.custom_fields) {
        if (!field.fieldValue) {
          return false;
        }
      }
    }
    return true;
  }

  // Check for extra projects and personal data
  async function handleFormSubmit(data: FieldValues) {
    const persoDataComplete = allPersoValuesFilled(
      instoreOrder
        ? (instoreOrderLineRequests ?? [])
        : instoreOrderLinesFromTemplate,
    );
    let showModal = false;

    // Check if comment is missing or empty
    if (!data.comment || data.comment.trim() === "") {
      setNoExtrasProjects(true);
      showModal = true;
    }

    // Check if all perso data is filled in
    if (!persoDataComplete) {
      setMissingPersoData(true);
      showModal = true;
    }

    // If either check failed, set pending data and show modal
    if (showModal) {
      setPendingSubmitData(data);
      dispatch(openModal(INSTORE_ORDER_WITHOUT_EXTRAS_SURE));
      return;
    }

    confirmFormSubmit(data);
  }

  // Confirm submit handler
  async function confirmFormSubmit(data: FieldValues) {
    await submitForm(data);
    cancelModals();
  }

  // Submit form
  async function submitForm(data: FieldValues) {
    const request: InstoreOrderRequest = {
      order_status: instoreOrder?.order_status_name ?? "Ordered",
      order_code: instoreOrder
        ? instoreOrder.order_code // keep original code (date unchanged)
        : `${data.order_type === "1" ? "OPEN" : "RELO"}__${
            data.external_store_id
          }__${format(new Date(), "yyyyMMddHHmm")}`,
      order_type_instore_id:
        instoreOrderTypes?.find(
          (iOt) => iOt.name === data.order_type_instore_name,
        )?.id ?? 0,
      order_date: instoreOrder?.order_date
        ? toBackendDate(instoreOrder?.order_date)
        : format(new Date(), "yyyy-MM-dd HH:mm"),
      instore_type_id:
        instoreTypes?.find((it) => it.name === data.instore_type)?.id ?? 0,
      store_format_id:
        instoreType?.store_formats?.find((sf) => sf.name === data.store_format)
          ?.id ?? 0,
      delivery_date: data.delivery_date ?? undefined,
      comment: data.comment,
      track_and_trace: data.track_and_trace,
      extra_promos: data.extra_promos,
      template_id: instoreOrder
        ? instoreOrder.template_id
        : (templateToUse?.id ?? 0),
      store_id: data.store_id,
      external_store_id: data.external_store_id,
      store_name: data.store_name,
      store_opening_date: data.store_opening_date,
      store_language: data.store_language,
      store_contact_first_name: data.store_contact_first_name,
      store_contact_last_name: data.store_contact_last_name,
      store_contact_phone: data.store_contact_phone,
      store_contact_email: data.store_contact_email,
      store_addres_street_1: data.store_addres_street_1,
      store_addres_street_2: data.store_addres_street_2,
      store_addres_street_3: data.store_addres_street_3,
      store_addres_zip_code: data.store_addres_zip_code,
      store_addres_city: data.store_addres_city,
      store_addres_country: data.store_addres_country,
      delivery_addres_street_1: data.delivery_addres_street_1,
      delivery_addres_street_2: data.delivery_addres_street_2,
      delivery_addres_street_3: data.delivery_addres_street_3,
      delivery_addres_zip_code: data.delivery_addres_zip_code,
      delivery_addres_city: data.delivery_addres_city,
      delivery_addres_country: data.delivery_addres_country,
      delivery_contact_email: data.delivery_contact_email,
      store_digital_frame:
        data.store_digital_frame === NONE_OPTION_VALUE
          ? null
          : data.store_digital_frame,
      instore_order_lines: instoreOrder
        ? undefined
        : instoreOrderLinesFromTemplate.map((iOL) => ({
            id: iOL.id,
            theme_id: iOL.theme_id,
            theme_name: iOL.theme_name,
            store_format_id: iOL.store_format_id,
            store_format_name: iOL.store_format_name,
            article_name: iOL.article_name,
            amount: iOL.amount,
            custom_fields: iOL.custom_fields,
            index: iOL.index,
          })),
    };

    try {
      if (instoreOrder) {
        await dispatch(
          updateInstoreOrderAsync({
            id: instoreOrder.id,
            values: { ...request },
          }),
        );

        instoreOrderLineRequests &&
          (await dispatch(
            updateManyInstoreOrderLinesAsync({
              instoreOrder_id: instoreOrder.id,
              values: {
                instore_type_id: instoreOrder.instore_type_id,
                instore_order_lines: instoreOrderLineRequests.map((iOL) => ({
                  id: iOL.id,
                  theme_id: iOL.theme_id,
                  theme_name: iOL.theme_name,
                  store_format_id: iOL.store_format_id,
                  store_format_name: iOL.store_format_name,
                  article_name: iOL.article_name,
                  amount: iOL.amount,
                  custom_fields: iOL.custom_fields,
                  index: iOL.index,
                })),
              },
            }),
          ));
      } else {
        await dispatch(
          createInstoreOrderAsync({
            values: { ...request },
          }),
        );
      }
      cancelCUD();
    } catch (error) {
      toast.error(`${error}`);
    }
  }
  //#endregion

  //#region COMMENT CRUD
  const addCommentModalHandler = () => {
    dispatch(openModal(ADD_COMMENT_MODAL));
  };

  const setCommentsHandler = (items: string[]) => {
    setValue("comment", items.join(";"));
    cancelModals();
  };

  const removeCommentHandler = (value: string) => {
    const currentComments = comments || "";
    const updatedComments = currentComments
      .split(";")
      .map((c: string) => c.trim())
      .filter((c: string) => c !== value)
      .join(";");

    setValue("comment", updatedComments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };
  //#endregion

  //#region TRACK AND TRACE CRUD
  const addTrackAndTraceModalHandler = () => {
    dispatch(openModal(ADD_TRACK_AND_TRACE_MODAL));
  };

  const setTrackAndTracesHandler = (items: string[]) => {
    setValue("track_and_trace", items.join(";"));
    cancelModals();
  };

  const removeTrackAndTraceHandler = (value: string) => {
    const currentTrackAndTraces = trackAndTrace || "";
    const updatedTrackAndTraces = currentTrackAndTraces
      .split(";")
      .map((x: string) => x.trim())
      .filter((x: string) => x !== value)
      .join(";");

    setValue("track_and_trace", updatedTrackAndTraces, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };
  //#endregion

  //#region SEARCH TEMPLATE
  const enableTemplateSearch =
    !!orderTypeInstoreNameValue &&
    !!storeOpeningDateValue &&
    !!instoreTypeValue &&
    !!storeFormatValue;

  const searchTemplateHandler = async () => {
    const request: FindTemplateRequest = {
      order_type_instore_id:
        instoreOrderTypes?.find(
          (iOt) => iOt.name === getValues("order_type_instore_name"),
        )?.id ?? 0,
      date: getValues("store_opening_date"),
      instore_type_id:
        instoreTypes?.find((it) => it.name === getValues("instore_type"))?.id ??
        0,
      store_format_id:
        instoreType?.store_formats?.find(
          (sf) => sf.name === getValues("store_format"),
        )?.id ?? 0,
    };

    try {
      const { template } = await dispatch(
        fetchMatchingInstoreTemplateAsync(request),
      ).unwrap();
      setValue("template_id", template.id);
      setValue("template", template.name, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
      setInstoreTemplateHandler(template);
    } catch (error) {
      setInstoreTemplateHandler(undefined);
    }
  };
  //#endregion

  //#region SEARCH STORE
  // seach store
  const searchStoreModalHandler = () => {
    dispatch(openModal(SEARCH_STORE_MODAL));
  };

  // Set store
  const setStoreHandler = (store: Store) => {
    if (!store) {
      toast.error("No stores found");
    } else {
      setStoreToUse(store);
    }
    dispatch(openModal(false));
  };
  //#endregion

  //#region UI cleanup
  const cancelModals = () => {
    dispatch(openModal(false));
  };

  const cancelCUD = () => {
    history.goBack();
  };
  //#endregion
  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal
        type={INSTORE_ORDER_WITHOUT_EXTRAS_SURE}
        closeHandler={cancelModals}
      >
        <AreYouSure
          confirmHandler={() =>
            pendingSubmitData && confirmFormSubmit(pendingSubmitData)
          }
          closeHandler={cancelModals}
          disabled={disabled || isSubmitting}
          message={
            missingPersoData && noExtrasProjects
              ? "Some personal data is missing and you have no extra projects. Are you sure you want to proceed?"
              : missingPersoData
                ? "Some personal data is missing. Are you sure you want to proceed?"
                : noExtrasProjects
                  ? "You have no extra projects. Are you sure you want to proceed?"
                  : "Are you sure you want to proceed?"
          }
          title={
            missingPersoData && noExtrasProjects
              ? "Missing data & no extra projects"
              : missingPersoData
                ? "Missing personal data"
                : noExtrasProjects
                  ? "No extra projects"
                  : "Confirm order"
          }
        />
      </MainModal>

      <MainModal type={SEARCH_STORE_MODAL} closeHandler={cancelModals}>
        <InstoreOrderSearchStoreForm
          setStoreHandler={(store: Store) => setStoreHandler(store)}
          cancel={cancelModals}
        />
      </MainModal>

      <MainModal type={ADD_COMMENT_MODAL} closeHandler={cancelModals}>
        <MainFieldArrayForm
          onSubmitHandler={(items: string[]) => setCommentsHandler(items)}
          initialItems={
            comments !== ""
              ? comments?.split(";").map((value: string) => value)
              : []
          }
          propertyKey="commentText"
          options={instoreOrderPresetComments}
          fieldName="commentItems"
          cancel={cancelModals}
        />
      </MainModal>
      <MainModal type={ADD_TRACK_AND_TRACE_MODAL} closeHandler={cancelModals}>
        <MainFieldArrayForm
          onSubmitHandler={(items: string[]) => setTrackAndTracesHandler(items)}
          initialItems={
            trackAndTrace !== ""
              ? trackAndTrace?.split(";").map((value: string) => value)
              : []
          }
          propertyKey="trackAndTraceText"
          fieldName="trackAndTraceItems"
          cancel={cancelModals}
        />
      </MainModal>

      {/* MAIN */}
      <main className="mx-auto max-w-2xl p-6 lg:max-w-full bg-gray-50">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-16 pb-24"
        >
          {/* ORDER SPECIFICATIONS */}
          <section className="space-y-6">
            <h2 className="text-lg font-medium text-sky-900">
              Order specifications
            </h2>

            {/* Fields */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-8">
              {/* order_type_instore_name */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="order_type_instore_name"
                  className={classNames(
                    errors.order_type_instore_name
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.order_type_instore_name?.message || (
                    <Fragment>
                      order type
                      {!orderTypeInstoreNameValue && !instoreOrder && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  {!!instoreOrder ? (
                    <p>{instoreOrder.order_type_instore_name}</p>
                  ) : null}
                  <select
                    id="function"
                    defaultValue=""
                    className={classNames(
                      !!instoreOrder ? "hidden" : "block",
                      "w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6",
                    )}
                    disabled={formDisabled || !!instoreOrder}
                    {...register("order_type_instore_name", {
                      required: "This is a required field",
                    })}
                  >
                    <option value="" disabled hidden className="text-red-800">
                      Select a order type
                    </option>

                    {instoreOrderTypes_Loaded && (
                      <Fragment>
                        {instoreOrderTypes
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((type: InstoreOrderType) => (
                            <option key={type.id} value={type.name}>
                              {type.name} - {type.short_name}
                            </option>
                          ))}
                      </Fragment>
                    )}
                  </select>
                </div>
              </fieldset>

              {/* store_opening_date */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_opening_date"
                  className={classNames(
                    errors.store_opening_date
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_opening_date?.message || (
                    <Fragment>
                      opening date
                      {!storeOpeningDateValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>

                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="date"
                    {...register("store_opening_date", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* instore_type */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="instore_type"
                  className={classNames(
                    errors.instore_type ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.instore_type?.message || (
                    <Fragment>
                      line of business
                      {!instoreTypeValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <select
                    id="function"
                    defaultValue=""
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    {...register("instore_type", {
                      required: "This is a required field",
                    })}
                  >
                    <option value="" disabled hidden className="text-red-800">
                      Select a line of business
                    </option>

                    {instoreTypes_Loaded && (
                      <Fragment>
                        {instoreTypes
                          .slice() // copy to avoid mutating original array
                          .sort((a, b) => a.name.localeCompare(b.name)) // alphabetical
                          .map((type: InstoreType) => (
                            <option
                              key={type.id}
                              value={type.name}
                              disabled={type.store_formats.length <= 0}
                            >
                              {type.name}
                            </option>
                          ))}
                      </Fragment>
                    )}
                  </select>
                </div>
              </fieldset>

              {/* store_format */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_format"
                  className={classNames(
                    errors.store_format ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_format?.message || (
                    <Fragment>
                      store format
                      {!storeFormatValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}

                  {!instoreType ? (
                    <span className="text-red-600">
                      Select a line of business first
                    </span>
                  ) : null}
                </label>
                <div className="relative mt-2">
                  <select
                    id="function"
                    defaultValue=""
                    className={classNames(
                      formDisabled || !instoreType
                        ? "opacity-30 cursor-not-allowed"
                        : "opacity-100",
                      "block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6",
                    )}
                    disabled={formDisabled || !instoreType}
                    {...register("store_format", {
                      required: "This is a required field",
                      onChange: (e) =>
                        setStoreFormatHandler({
                          id:
                            instoreType?.store_formats?.find(
                              (isf) => isf.name === e.target.value,
                            )?.id ?? 0,
                          name: e.target.value,
                          customer_id: 11,
                          customer_name: "Rituals",
                        }),
                    })}
                  >
                    <option value="" disabled hidden>
                      Select store format
                    </option>
                    {instoreType && instoreType.store_formats.length > 0 && (
                      <Fragment>
                        {instoreType.store_formats
                          .slice() // copy to avoid mutating original array
                          .sort((a, b) => a.name.localeCompare(b.name)) // alphabetical
                          .map((format: StoreFormat) => (
                            <option key={format.id} value={format.name}>
                              {format.name}
                            </option>
                          ))}
                      </Fragment>
                    )}
                  </select>
                </div>
              </fieldset>

              {/* comment */}
              <div className="sm:col-span-8 flex items-center flex-wrap gap-2 mt-2">
                {comments !== "" &&
                  comments
                    ?.split(";")
                    .map((value: string, i: number) => (
                      <InstoreOrderFormCommentBadge
                        key={i}
                        value={value}
                        removeHandler={removeCommentHandler}
                      />
                    ))}
              </div>

              <fieldset className="sm:col-span-8">
                <div className="relative mt-2 flex items-center gap-4">
                  <input
                    placeholder="e.g. 1500"
                    className="hidden block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    {...register("comment")}
                  />
                  <button
                    type="button"
                    onClick={addCommentModalHandler}
                    className="bg-sky-900 hover:bg-sky-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-900 w-60 flex items-center gap-x-1.5 rounded-md  px-2.5 py-1.5 text-sm font-semibold text-white"
                  >
                    <svg
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.99 14.993 6-6m6 3.001c0 1.268-.63 2.39-1.593 3.069a3.746 3.746 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043 3.745 3.745 0 0 1-3.068 1.593c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 0 1-3.296-1.043 3.746 3.746 0 0 1-1.043-3.297 3.746 3.746 0 0 1-1.593-3.068c0-1.268.63-2.39 1.593-3.068a3.746 3.746 0 0 1 1.043-3.297 3.745 3.745 0 0 1 3.296-1.042 3.745 3.745 0 0 1 3.068-1.594c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.297 3.746 3.746 0 0 1 1.593 3.068ZM9.74 9.743h.008v.007H9.74v-.007Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                      />
                    </svg>
                    Add extra projects
                  </button>
                </div>
              </fieldset>

              {/* STORE INFORMATION */}
              <h2 className="text-lg font-medium text-sky-900 sm:col-span-8 mt-12">
                Store information
              </h2>
              {/* external_store_id */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="external_store_id"
                  className={classNames(
                    errors.external_store_id ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.external_store_id?.message || (
                    <Fragment>
                      store number
                      {!externalStoreIdValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={searchStoreModalHandler}
                    className={classNames(
                      !!storeToUse
                        ? "bg-gray-300 hover:bg-gray-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
                        : "bg-sky-900 hover:bg-sky-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-900",
                      "w-32 flex items-center gap-x-1.5 rounded-md  px-2.5 py-1.5 text-sm font-semibold text-white",
                    )}
                  >
                    <MagnifyingGlassCircleIcon className="w-6 h-6" />
                    Search
                  </button>
                  <input
                    placeholder="e.g. 1500"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    {...register("external_store_id", {
                      required: "The store id is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* store_name */}
              <fieldset className="sm:col-span-3">
                <label
                  htmlFor="store_name"
                  className={classNames(
                    errors.store_name ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_name?.message || (
                    <Fragment>
                      store name
                      {!storeNameValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. Rituals Amsterdam"
                    {...register("store_name", {
                      required: "The store name is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* store_addres_street_1 */}
              <fieldset className="sm:col-span-3">
                <label
                  htmlFor="store_addres_street_1"
                  className={classNames(
                    errors.store_addres_street_1
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_addres_street_1?.message || (
                    <Fragment>
                      address line
                      {!storeAddresStreet1Value && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. Brusselse steenweg"
                    {...register("store_addres_street_1", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* store_addres_street_2 */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_addres_street_2"
                  className={classNames(
                    errors.store_addres_street_2
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 ",
                  )}
                >
                  {errors.store_addres_street_2
                    ? errors?.store_addres_street_2?.message
                    : "address line 2"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. number 12"
                    {...register("store_addres_street_2")}
                  />
                </div>
              </fieldset>

              {/* store_addres_street_3 */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_addres_street_3"
                  className={classNames(
                    errors.store_addres_street_3
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 ",
                  )}
                >
                  {errors.store_addres_street_3
                    ? errors?.store_addres_street_3?.message
                    : "address line 3"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. bus 44"
                    {...register("store_addres_street_3")}
                  />
                </div>
              </fieldset>

              {/* store_addres_zip_code */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_addres_zip_code"
                  className={classNames(
                    errors.store_addres_zip_code
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_addres_zip_code?.message || (
                    <Fragment>
                      zip code
                      {!storeAddresZipCodeValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. 9000"
                    {...register("store_addres_zip_code", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* store_addres_city */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_addres_city"
                  className={classNames(
                    errors.store_addres_city ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_addres_city?.message || (
                    <Fragment>
                      city
                      {!storeAddresCityValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. Gent"
                    {...register("store_addres_city", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* store_addres_country */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_addres_country"
                  className={classNames(
                    errors.store_addres_country
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_addres_country?.message || (
                    <Fragment>
                      country
                      {!storeAddresCountryValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <select
                    id="function"
                    defaultValue="Retail"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    {...register("store_addres_country", {
                      required: "This is a required field",
                    })}
                  >
                    {countries_loaded && (
                      <Fragment>
                        {countries
                          .slice() // copy to avoid mutating original array
                          .sort((a, b) => a.name.localeCompare(b.name)) // alphabetical
                          .map((country: Country) => (
                            <option key={country.id} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                      </Fragment>
                    )}
                  </select>
                </div>
              </fieldset>

              {/* store_language */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_language"
                  className={classNames(
                    errors.store_language ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.store_language?.message || (
                    <Fragment>
                      store language
                      {!storeLanguageValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <select
                    id="function"
                    defaultValue="Retail"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    {...register("store_language", {
                      required: "This is a required field",
                    })}
                  >
                    {languages_loaded && (
                      <Fragment>
                        {languages
                          .slice()
                          .sort((a, b) => a.code.localeCompare(b.code))
                          .map((language: Language) => (
                            <option key={language.id} value={language.code}>
                              {language.code}
                            </option>
                          ))}
                      </Fragment>
                    )}
                  </select>
                </div>
              </fieldset>

              {/* store_contact_email */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_contact_email"
                  className={classNames(
                    errors.store_contact_email
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 ",
                  )}
                >
                  {errors.store_contact_email
                    ? errors?.store_contact_email?.message
                    : "store email address"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="email"
                    placeholder="e.g. johnDoe@email.com"
                    {...register("store_contact_email")}
                  />
                </div>
              </fieldset>

              {/* digital_frame */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="digital_frame"
                  className={classNames(
                    errors.instore_type ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 ",
                  )}
                >
                  {errors.digital_frame
                    ? errors?.digital_frame?.message
                    : "digital frame"}
                </label>
                <div className="relative mt-2">
                  <select
                    defaultValue=""
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    {...register("store_digital_frame")}
                  >
                    <option value="" disabled hidden>
                      Select digital frame
                    </option>
                    {digitalFrameValue && (
                      <option value={NONE_OPTION_VALUE}>
                        {NONE_OPTION_VALUE}
                      </option>
                    )}

                    {digitalFramesLoaded &&
                      digitalFrames?.map((frame) => (
                        <option key={frame.id} value={frame.name}>
                          {frame.name}
                        </option>
                      ))}
                  </select>
                </div>
              </fieldset>

              {/* DELIVERY INFORMATION */}
              <h2 className="text-lg font-medium text-sky-900 sm:col-span-6 mt-12">
                Temporary delivery address
              </h2>

              {/* delivery_addres_street_1 */}
              <fieldset className="sm:col-span-4">
                <label
                  htmlFor="delivery_addres_street_1"
                  className={classNames(
                    errors.delivery_addres_street_1
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.delivery_addres_street_1?.message || (
                    <Fragment>
                      address line
                      {!deliveryAddresStreet1Value && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. Brusselse steenweg"
                    {...register("delivery_addres_street_1", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* delivery_addres_street_2 */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="delivery_addres_street_2"
                  className={classNames(
                    errors.delivery_addres_street_2
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6",
                  )}
                >
                  {errors.delivery_addres_street_2
                    ? errors?.delivery_addres_street_2?.message
                    : "address line 2"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. number 12"
                    {...register("delivery_addres_street_2")}
                  />
                </div>
              </fieldset>

              {/* delivery_addres_street_3 */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="delivery_addres_street_3"
                  className={classNames(
                    errors.delivery_addres_street_3
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6",
                  )}
                >
                  {errors.delivery_addres_street_3
                    ? errors?.delivery_addres_street_3?.message
                    : "address line 3"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. bus 44"
                    {...register("delivery_addres_street_3")}
                  />
                </div>
              </fieldset>

              {/* delivery_addres_zip_code */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="delivery_addres_zip_code"
                  className={classNames(
                    errors.delivery_addres_zip_code
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.delivery_addres_zip_code?.message || (
                    <Fragment>
                      zip code
                      {!deliveryAddresZipCodeValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. 9000"
                    {...register("delivery_addres_zip_code", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* delivery_addres_city */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="delivery_addres_city"
                  className={classNames(
                    errors.delivery_addres_city
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.delivery_addres_city?.message || (
                    <Fragment>
                      city
                      {!deliveryAddresCityValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. Gent"
                    {...register("delivery_addres_city", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* delivery_addres_country */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="delivery_addres_country"
                  className={classNames(
                    errors.delivery_addres_country
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.delivery_addres_country?.message || (
                    <Fragment>
                      country
                      {!deliveryAddresCountryValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <select
                    id="function"
                    defaultValue="Retail"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    {...register("delivery_addres_country", {
                      required: "This is a required field",
                    })}
                  >
                    {countries_loaded && (
                      <Fragment>
                        {countries
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((country: Country) => (
                            <option key={country.id} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                      </Fragment>
                    )}
                  </select>
                </div>
              </fieldset>

              {/* delivery_contact_email */}
              <fieldset className="sm:col-span-3">
                <label
                  htmlFor="delivery_contact_email"
                  className={classNames(
                    errors.delivery_contact_email
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6",
                  )}
                >
                  {errors.delivery_contact_email
                    ? errors?.delivery_contact_email?.message
                    : "email for track and trace"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="email"
                    placeholder="e.g. johnDoe@email.com"
                    {...register("delivery_contact_email")}
                  />
                </div>
              </fieldset>

              {/* store_contact_first_name */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_contact_first_name"
                  className={classNames(
                    errors.store_contact_first_name
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6",
                  )}
                >
                  {errors.store_contact_first_name
                    ? errors?.store_contact_first_name?.message
                    : "first name"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. John"
                    {...register("store_contact_first_name")}
                  />
                </div>
              </fieldset>

              {/* store_contact_last_name */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_contact_last_name"
                  className={classNames(
                    errors.store_contact_last_name
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6",
                  )}
                >
                  {errors.store_contact_last_name
                    ? errors?.store_contact_last_name?.message
                    : "last name"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    placeholder="e.g. Doe"
                    {...register("store_contact_last_name")}
                  />
                </div>
              </fieldset>

              {/* store_contact_phone */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="store_contact_phone"
                  className={classNames(
                    errors.store_contact_phone
                      ? "text-red-500"
                      : "text-gray-900",
                    "block text-sm font-medium leading-6",
                  )}
                >
                  {errors.store_contact_phone
                    ? errors?.store_contact_phone?.message
                    : "phone number"}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="tel"
                    placeholder="e.g. +323 830 30 00"
                    {...register("store_contact_phone")}
                  />
                </div>
              </fieldset>

              {/* delivery_date */}
              <fieldset className="sm:col-span-2">
                <label
                  htmlFor="delivery_date"
                  className={classNames(
                    errors.delivery_date ? "text-red-500" : "text-gray-900",
                    "block text-sm font-medium leading-6 flex items-center gap-1",
                  )}
                >
                  {errors.delivery_date?.message || (
                    <Fragment>
                      requested delivery date
                      {!deliveryDateValue && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  )}
                </label>
                <div className="relative mt-2">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="date"
                    {...register("delivery_date", {
                      required: "This is a required field",
                    })}
                  />
                </div>
              </fieldset>

              {/* TRACKING */}
              <h2 className="text-lg font-medium text-sky-900 sm:col-span-8 mt-12">
                Parcel tracking
              </h2>

              <div className="sm:col-span-8 flex items-center flex-wrap gap-2">
                {trackAndTrace !== "" &&
                  trackAndTrace
                    ?.split(";")
                    .map((value: string, index: number) => (
                      <InstoreOrderFormTrackAndTraceBadge
                        key={index}
                        index={index}
                        value={value}
                        removeHandler={
                          isAdmin ? removeTrackAndTraceHandler : undefined
                        }
                      />
                    ))}
              </div>

              {/* track_and_trace */}
              <fieldset className="sm:col-span-8">
                <div className="relative mt-2 flex items-center gap-4">
                  <input
                    placeholder="e.g. 1500"
                    className="hidden block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                    disabled={formDisabled}
                    type="text"
                    {...register("track_and_trace")}
                  />
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={addTrackAndTraceModalHandler}
                      className="bg-emerald-800 hover:bg-emerald-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 w-60 flex items-center gap-x-1.5 rounded-md  px-2.5 py-1.5 text-sm font-semibold text-white"
                    >
                      <TruckIcon className="w-6 h-6" />
                      Add track and trace codes
                    </button>
                  ) : null}
                </div>
              </fieldset>
            </div>
          </section>

          {/* ORDER SUMMARY */}
          <section className="space-y-6">
            <h2 className="text-lg font-medium text-sky-900">Order summary</h2>
            {/* template */}
            <fieldset className="w-full">
              <label
                htmlFor="template"
                className={classNames(
                  errors.template ? "text-red-500" : "text-gray-900",
                  "block text-sm font-medium leading-6 flex items-center gap-1",
                )}
              >
                {errors.template?.message ||
                  (instoreOrder ? null : (
                    <Fragment>
                      template
                      {!templateValue && !instoreOrder && (
                        <StarIcon className="h-2 w-2 text-red-500" />
                      )}
                    </Fragment>
                  ))}
              </label>
              <div className="relative mt-2 flex items-center gap-4">
                <input
                  placeholder="e.g. 1500"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6 hidden"
                  disabled={!enableTemplateSearch}
                  type="text"
                  {...register("template", {
                    required: !instoreOrder
                      ? "The template id is a required field"
                      : false,
                  })}
                />
                {instoreOrder ? null : (
                  <Fragment>
                    <button
                      type="button"
                      onClick={searchTemplateHandler}
                      disabled={!enableTemplateSearch}
                      className={classNames(
                        !enableTemplateSearch
                          ? "bg-gray-300 cursore-not-allowed"
                          : "bg-sky-900 hover:bg-sky-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-900",
                        "w-60 flex items-center gap-x-1.5 rounded-md  px-2.5 py-1.5 text-sm font-semibold text-white",
                      )}
                    >
                      <MagnifyingGlassCircleIcon className="w-6 h-6" />
                      Search matching template
                    </button>
                    {templateToUse ? (
                      <h4 className="font-bold text-lg">
                        {templateToUse.name}
                      </h4>
                    ) : null}
                  </Fragment>
                )}
              </div>
            </fieldset>
            <div className="border border-gray-200 bg-white">
              {orderLineItemsList}
            </div>
          </section>

          <section>
            {/* Actions */}
            <div className="border-t border-gray-200 mt-16">
              <button
                type="submit"
                disabled={formDisabled || !isValid}
                className={classNames(
                  formDisabled || !isValid
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-sky-900 hover:bg-sky-800 focus:ring-2 focus:ring-sky-800 focus:ring-offset-2 focus:ring-offset-gray-50 focus:outline-hidden",
                  "w-full rounded-md border border-transparent px-4 py-3 text-base font-medium text-white",
                )}
              >
                {isSubmitting
                  ? "Saving order..."
                  : instoreOrder
                    ? "Save order"
                    : "Place order"}
              </button>
            </div>
          </section>
        </form>
      </main>
    </Fragment>
  );
  //#endregion
}
